import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';

export type UniversalHeroKind = 'jesus' | 'mary' | 'peter' | 'judas' | 'pilate';

const loader = new GLTFLoader();
const cache = new Map<string, Promise<GLTF>>();

const FILES: Record<UniversalHeroKind, string> = {
  jesus: '/assets/3d/universal-base/Jesus_PeasantFirstCentury.glb',
  mary: '/assets/3d/universal-base/Mary_PeasantFirstCentury.glb',
  peter: '/assets/3d/universal-base/Peter_PeasantFirstCentury.glb',
  judas: '/assets/3d/universal-base/Judas_PeasantFirstCentury.glb',
  pilate: '/assets/3d/universal-base/Pilate_PeasantFirstCentury.glb',
};

const HEAD_FALLBACKS: Record<UniversalHeroKind, string> = {
  jesus: '/assets/3d/universal-base/Jesus_DetailedHead.glb',
  mary: '/assets/3d/universal-base/Mary_DetailedHead.glb',
  peter: '/assets/3d/universal-base/Peter_DetailedHead.glb',
  judas: '/assets/3d/universal-base/Judas_DetailedHead.glb',
  pilate: '/assets/3d/universal-base/Pilate_DetailedHead.glb',
};

const JESUS_CRUCIFIXION_BODY = '/assets/3d/universal-base/Jesus_CrucifixionBody.glb';

const ROLE_SCALE: Record<UniversalHeroKind, [number, number, number]> = {
  // Small proportional changes keep the shared skeleton coherent while making
  // the cast legible in silhouette: Jesus lean/tall, Peter broad/grounded,
  // Judas slight, Mary compact, and Pilate upright without heroic exaggeration.
  jesus: [1.13, 1.18, 1.12],
  mary: [1.1, 1.15, 1.08],
  peter: [1.2, 1.13, 1.17],
  judas: [1.09, 1.16, 1.08],
  pilate: [1.15, 1.17, 1.13],
};

const CLOTH_PALETTE: Record<UniversalHeroKind, { body: number; sleeves: number; legs: number; feet: number }> = {
  jesus: { body: 0xa09277, sleeves: 0xb0a48b, legs: 0x544a3d, feet: 0x30251d },
  mary: { body: 0x592d38, sleeves: 0x663745, legs: 0x432832, feet: 0x30231f },
  peter: { body: 0x4b5141, sleeves: 0x61634f, legs: 0x30372f, feet: 0x261e18 },
  judas: { body: 0x67633d, sleeves: 0x72503a, legs: 0x44382b, feet: 0x2b2119 },
  pilate: { body: 0x7c312e, sleeves: 0xb9aa8c, legs: 0x76644e, feet: 0x30231d },
};

const SKIN_TINT: Record<UniversalHeroKind, number> = {
  jesus: 0xb8896e,
  mary: 0xb17f69,
  peter: 0xa8755e,
  judas: 0xb78368,
  pilate: 0xc29179,
};

function source(url: string): Promise<GLTF> {
  let pending = cache.get(url);
  if (!pending) {
    pending = loader.loadAsync(url);
    cache.set(url, pending);
  }
  return pending;
}

function trimMaryHeadUnderlay(mesh: THREE.Mesh): void {
  if (mesh.name !== 'Mary_Detailed_Head') return;
  const geometry = mesh.geometry;
  const positions = geometry.getAttribute('position');
  const index = geometry.index;
  if (!(positions instanceof THREE.BufferAttribute) || !index) return;

  // The source head extraction retained a handful of upper-torso triangles
  // because they carried small neck-bone weights. Remove only triangle centers
  // below the jaw, preserving a narrow central neck bridge. This keeps Mary's
  // detailed face while preventing superhero chest anatomy from protruding
  // through the period tunic.
  const kept: number[] = [];
  for (let offset = 0; offset < index.count; offset += 3) {
    const a = index.getX(offset);
    const b = index.getX(offset + 1);
    const c = index.getX(offset + 2);
    const centerX = (positions.getX(a) + positions.getX(b) + positions.getX(c)) / 3;
    const centerY = (positions.getY(a) + positions.getY(b) + positions.getY(c)) / 3;
    const centerZ = (positions.getZ(a) + positions.getZ(b) + positions.getZ(c)) / 3;
    const faceOrHairline = centerY >= 1.49;
    const centralNeck = centerY >= 1.405 && Math.abs(centerX) < 0.068 && centerZ < 0.055;
    if (faceOrHairline || centralNeck) kept.push(a, b, c);
  }
  const trimmed = geometry.clone();
  trimmed.setIndex(kept);
  trimmed.clearGroups();
  trimmed.addGroup(0, kept.length, 0);
  trimmed.computeBoundingBox();
  trimmed.computeBoundingSphere();
  mesh.geometry = trimmed;
}

function prepare(kind: UniversalHeroKind, body: GLTF, detailsOnly: boolean): THREE.Group {
  const model = new THREE.Group();
  model.name = `universal-base:${kind}`;
  model.userData.detailsOnly = detailsOnly;
  model.add(cloneSkeleton(body.scene));
  model.updateMatrixWorld(true);

  // Head-only exports retain the original Universal Base coordinate system.
  // Role-specific non-uniform scaling stays subtle enough for the common rig
  // while preventing every principal from sharing one superhero proportion.
  model.scale.set(...ROLE_SCALE[kind]);

  model.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    if (kind === 'mary') trimMaryHeadUnderlay(child);
    child.castShadow = true;
    child.receiveShadow = true;
    child.frustumCulled = true;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of materials) {
      if (material instanceof THREE.MeshStandardMaterial) {
        const name = material.name.toLowerCase();
        const palette = CLOTH_PALETTE[kind];
        material.roughness = Math.max(0.8, material.roughness);
        material.metalness = 0;
        material.envMapIntensity = 0.35;
        if (name.includes('hair')) {
          // The archive intentionally ships white swatches for tinting.
          // Multiply them toward historically plausible dark hair rather than
          // inheriting the stock fantasy-white presentation.
          const hairColors: Record<UniversalHeroKind, number> = {
            jesus: 0x241812,
            mary: 0x1d1513,
            peter: 0x493a30,
            judas: 0x1f1410,
            pilate: 0x32251c,
          };
          material.color.set(hairColors[kind]);
        }
        if (name.includes('body_woven')) material.color.set(palette.body);
        if (name.includes('sleeves_woven')) material.color.set(palette.sleeves);
        if (name.includes('legs_woven')) material.color.set(palette.legs);
        if (name.includes('feet_dark')) material.color.set(palette.feet);
        if (name.includes('superhero') || name.includes('regular_male')) material.color.set(SKIN_TINT[kind]);
        if (name.includes('woven')) material.roughness = 0.96;
        material.emissive?.set(0x000000);
        if (material.map) {
          material.map.colorSpace = THREE.SRGBColorSpace;
          material.map.anisotropy = 8;
        }
      }
    }
  });

  return model;
}

/** Clone the shared-rig CC0 peasant presentation, with a head-only fallback. */
export async function createUniversalHeroBase(kind: UniversalHeroKind): Promise<THREE.Group> {
  try {
    return prepare(kind, await source(FILES[kind]), false);
  } catch (error) {
    console.warn(`Shared-rig ${kind} asset unavailable; loading the detailed-head fallback.`, error);
    return prepare(kind, await source(HEAD_FALLBACKS[kind]), true);
  }
}

/** Detailed head used over the dedicated procedural crucifixion anatomy. */
export async function createUniversalHeroHead(kind: UniversalHeroKind): Promise<THREE.Group> {
  return prepare(kind, await source(HEAD_FALLBACKS[kind]), true);
}

/** One continuous CC0 body/head skeleton authored specifically for Golgotha. */
export async function createJesusCrucifixionBody(): Promise<THREE.Group> {
  const model = prepare('jesus', await source(JESUS_CRUCIFIXION_BODY), false);
  model.name = 'universal-base:jesus-crucifixion';
  return model;
}
